import React, { useState } from 'react';
import { Stack, Typography, Popover } from '@mui/material';
import ProjectData from 'components/CalendarDayTile/ProjectData/ProjectData';
import {
  useUpdateWorkingHoursMutation,
  useDeleteWorkingHoursMutation,
} from 'store/workingHours/workingHours';
import { millisecondsInSecond, secondsInHour } from 'helpers/timeConsts';
import { useAppDispatch } from 'store/hooks';
import { FormPayload } from 'components/ScheduleCalendar/EditWorkingHoursForm/interfaces';
import getWorkingDuration from 'components/ScheduleCalendar/getWorkingDuration';
import EditWorkingHoursForm from 'components/ScheduleCalendar/EditWorkingHoursForm/EditWorkingHoursForm';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import replaceApiIri from 'helpers/replaceApiIri';
import checkIsDateMidnight from 'helpers/checkIsDateMidnight';
import { AppointmentContentProps } from '../interfaces';
import largeDurationTypeInHours from './consts';

export default function Appointment({
  data,
  durationType,
}: Pick<AppointmentContentProps, 'data' | 'durationType'>) {
  const dispatch = useAppDispatch();
  const [editAnchorEl, setEditAnchorEl] = useState<HTMLDivElement | null>(null);

  const [updateWorkingHours, { isLoading: updateWorkingHoursLoading }] =
    useUpdateWorkingHoursMutation();
  const [deleteWorkingHours] = useDeleteWorkingHoursMutation();

  const handleUpdateWorkingHours = async (payloadData: FormPayload) => {
    try {
      const { project, endDate, startDate } = payloadData;
      const { projectIri, phaseIri } = JSON.parse(project);

      const isEndDateMidnight = checkIsDateMidnight(endDate);

      const endDateInMsToCompare = isEndDateMidnight
        ? endDate.getTime() + 1
        : endDate.getTime();

      const seconds =
        (endDateInMsToCompare - new Date(startDate).getTime()) /
        millisecondsInSecond;

      await updateWorkingHours({
        id: data.id,
        project: projectIri,
        projectPhase: phaseIri || null,
        seconds,
        date: startDate,
      }).unwrap();
      dispatch(setSuccessStatus(true));
      setEditAnchorEl(null);
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  const handleDeleteWorkingHours = async () => {
    try {
      await deleteWorkingHours(data.id).unwrap();
      dispatch(setSuccessStatus(true));
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  return (
    <>
      <Stack
        direction={durationType === 'short' ? 'row' : 'column'}
        justifyContent="space-between"
        height="100%"
        py={data.seconds >= secondsInHour * largeDurationTypeInHours ? 2 : 0}
        px={2}
        onClick={(event) => {
          event.stopPropagation();
          setEditAnchorEl(event.currentTarget);
        }}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          cursor: 'pointer',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: 'primary.light',
          },
        }}
      >
        <ProjectData
          projectIri={data.project}
          projectPhaseIri={data.projectPhase}
          variant="body2"
          color="primary.dark"
          truncateLength={15}
        />
        <Typography variant="body2" color="primary.dark">
          {getWorkingDuration(data.seconds)}
        </Typography>
      </Stack>
      <Popover
        sx={{ '& .MuiPaper-root': { width: '38rem' } }}
        open={!!editAnchorEl}
        anchorEl={editAnchorEl}
        onClose={() => setEditAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <EditWorkingHoursForm
          isSubmitting={updateWorkingHoursLoading}
          onDelete={handleDeleteWorkingHours}
          onSubmit={handleUpdateWorkingHours}
          handleCloseModal={() => setEditAnchorEl(null)}
          employeeId={replaceApiIri(data.employee, 'employees')}
          initValues={{
            project: JSON.stringify({
              projectIri: data.project,
              ...(data.projectPhase && { phaseIri: data.projectPhase }),
            }),
            description: data.description,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
          }}
        />
      </Popover>
    </>
  );
}
